// SettingsPlanCatalogGrid.jsx

import React from 'react';

// Utilities
import { LIVE_ROOMS_SURFACE_ENABLED } from 'features/liveRoomsSurface';
import {
    formatApiSummary,
    formatLiveSummary,
    formatPlanPrice,
    formatStoragePerSeat,
    formatUserLimit,
    getPlanActionLabel,
    isSelfServeSelectable,
} from '../utils/settingsPlanPaneUtils';

// CSS
import './SettingsPlanCatalogGrid.css';

// Components
import Button from 'components/button/Button';

/**
 * SettingsPlanCatalogGrid
 *
 * Renders the public plan catalog with full limit details and upgrade/downgrade actions.
 *
 * @returns {ReactNode} The rendered plan catalog grid.
 */
const SettingsPlanCatalogGrid = React.memo(({
    canEditBase,
    currentPlan,
    currentPlanKey,
    hasActivePaidSubscription,
    isSubscriptionCancelScheduled,
    onSelectPlan,
    onStartDowngradeToPlan,
    onStartPaidCheckout,
    plans,
    savingPlanKey,
    theme,
}) => {

  // ----- RENDER ----- //

  return (
        <div className="settings-modal-plan-grid">
            {plans.map((plan) => {
                const planKey = String(plan?.key || '').trim();
                const isCurrent = planKey === currentPlanKey;
                const isSelectable = isSelfServeSelectable(plan);
                const isSaving = savingPlanKey === planKey;
                const canSelectFreePlan = canEditBase
                    && !isCurrent
                    && !savingPlanKey
                    && !hasActivePaidSubscription;
                const canDowngradeToFreePlan = canEditBase
                    && !isCurrent
                    && !savingPlanKey
                    && hasActivePaidSubscription
                    && !isSubscriptionCancelScheduled
                    && isSelectable;
                const canStartPaidCheckout = canEditBase
                    && !savingPlanKey
                    && !isCurrent
                    && !hasActivePaidSubscription
                    && !isSelectable;

                const canShowPlanAction = isSelectable
                    ? (hasActivePaidSubscription ? canDowngradeToFreePlan : canSelectFreePlan)
                    : canStartPaidCheckout;

                const actionLabel = getPlanActionLabel({
                    currentPlan,
                    hasActivePaidSubscription,
                    isCurrent,
                    isSaving,
                    isSelectable,
                    targetPlan: plan,
                });

                return (
                    <div
                      className="settings-modal-plan-card"
                      style={{
                            backgroundColor: isCurrent ? `${theme.highlight}77` : theme.panel,
                            borderColor: isCurrent ? theme.backgroundHover : theme.panel,
                        }}
                      key={planKey}
                    >

                        {/* PLAN CARD HEADER */}
                        <div className="settings-modal-plan-card-header">

                            {/* PLAN CARD META */}
                            <div className="settings-modal-plan-card-meta">

                                {/* PLAN BADGE */}
                                <div
                                  className="settings-modal-plan-badge"
                                >
                                    {isCurrent ? (
                                        <span>Current Plan</span>
                                    ) : (
                                        <span>Available</span>
                                    )}
                                </div>

                                {/* PLAN CARD TITLE ROW */}
                                <div className="settings-modal-plan-card-title-row">

                                    {/* PLAN CARD TITLE */}
                                    <div
                                      className="settings-modal-plan-card-title"
                                    >
                                        {plan.title}
                                    </div>

                                    {/* PLAN CARD PRICE */}
                                    <div
                                      className="settings-modal-plan-card-price"
                                    >
                                        {formatPlanPrice(plan)}
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className="settings-modal-plan-facts">
                            <div className="settings-modal-plan-fact">
                                <span
                                  className="settings-modal-plan-fact-label"
                                  style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    Users
                                </span>
                                <span
                                  className="settings-modal-plan-fact-value"
                                  style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {formatUserLimit(plan)}
                                </span>
                            </div>

                            <div className="settings-modal-plan-fact">
                                <span
                                  className="settings-modal-plan-fact-label"
                                  style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    Storage
                                </span>
                                <span
                                  className="settings-modal-plan-fact-value"
                                  style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {formatStoragePerSeat(plan)}
                                </span>
                            </div>

                            <div className="settings-modal-plan-fact">
                                <span
                                  className="settings-modal-plan-fact-label"
                                  style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    API
                                </span>
                                <span
                                  className="settings-modal-plan-fact-value"
                                  style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {formatApiSummary(plan)}
                                </span>
                            </div>

                            {/* Live rooms — restore with LIVE_ROOMS_SURFACE_ENABLED */}
                            {LIVE_ROOMS_SURFACE_ENABLED ? (
                                <div className="settings-modal-plan-fact">
                                    <span
                                      className="settings-modal-plan-fact-label"
                                      style={{
                                            color: theme.foregroundMuted,
                                        }}
                                    >
                                        Live rooms
                                    </span>
                                    <span
                                      className="settings-modal-plan-fact-value"
                                      style={{
                                            color: theme.foreground,
                                        }}
                                    >
                                        {formatLiveSummary(plan)}
                                    </span>
                                </div>
                            ) : null}

                            {/* RECORDING MINUTES
                            <div className="settings-modal-plan-fact">
                                <span
                                    className="settings-modal-plan-fact-label"
                                    style={{
                                        color: isCurrent ? theme.foreground : theme.foregroundMuted,
                                    }}
                                >
                                    Recording
                                </span>
                                <span
                                    className="settings-modal-plan-fact-value"
                                    style={{
                                        color: isCurrent ? theme.foreground : theme.foregroundMuted,
                                    }}
                                >
                                    {formatRecordingSummary(plan)}
                                </span>
                            </div>
                            */}

                        </div>

                        {/* PLAN FOOTER */}
                        {canShowPlanAction && (
                            <div className="settings-modal-plan-footer">
                                <Button
                                  label={actionLabel}
                                  enabled={canShowPlanAction}
                                  onClick={() => {
                                        if (isSelectable) {
                                            if (hasActivePaidSubscription) {
                                                onStartDowngradeToPlan(plan);
                                                return;
                                            }

                                            onSelectPlan(plan);
                                            return;
                                        }

                                        onStartPaidCheckout(plan);
                                    }}
                                />
                            </div>
                        )}

                    </div>
                );
            })}
        </div>
    );
});

// Dev Tools
SettingsPlanCatalogGrid.displayName = 'SettingsPlanCatalogGrid';

export default SettingsPlanCatalogGrid;
